import { createUseStyles } from 'react-jss';
import color from 'color';

import { ThemeProps } from '@styles/theme';
import { flex } from '@resources/mixins';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    expanded: {},
    notActive: {},
    mainWrapper: {
        height: '100%',
        [theme.down('mobile')]: {
            height: 'auto',
        },
        padding: `${theme.spacing(4)}px ${theme.spacing(8)}px ${theme.spacing(8)}px`,
        [theme.down('desktop')]: {
            padding: `${theme.spacing(3)}px ${theme.spacing(5)}px ${theme.spacing(5)}px`,
        },
        [theme.down('tablet')]: {
            padding: theme.spacing(4),
        },
    },
    body: {
        display: 'flex',
        padding: `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(10)}`,
    },

    header: {
        color: theme.colors.grey_text,
        fontSize: 16,
        fontFamily: theme.font.secondary,
        lineHeight: 1.25,
        paddingBottom: 10,
    },

    descriptionWrapper: {
        padding: `${0}px ${theme.spacing(2)}`,
        flexGrow: 1,
        minWidth: 200,
        maxWidth: 500,
    },

    devicesWrapper: {
        padding: `${0}px ${theme.spacing(2)}`,
        flexGrow: 1,
        maxWidth: 150,
    },

    accessWrapper: {
        flexGrow: 1,
        width: 200,
        padding: `${0}px ${theme.spacing(2)}`,
    },

    historyWrapper: {
        flexGrow: 1,
        flexShrink: 1,
        padding: `${0}px ${theme.spacing(2)}`,
    },

    text: {
        color: theme.colors.black,
        lineHeight: 1.5,
        fontSize: 16,
        fontFamily: theme.font.primary,
    },

    modesContainer: {
        ...flex('row', 'center', 'flex-start'),
    },

    mode: {
        fontSize: 18,
        backgroundColor: theme.colors.transp_violet,
        fontStyle: 'bold',
        padding: '6px 12px',
        marginRight: 8,
        borderRadius: theme.radius,
        fontFamily: theme.font.secondary,

        '&.MuiTypography-root': {
            // color: theme.colors.white,
            fontStyle: 'bold',
        },
    },

    expandIcon: {
        transformOrigin: 'center',
        transition: 'transform .2s ease',
        transform: 'rotate(90deg)',

        '&$expanded': {
            transform: 'rotate(-90deg)',
        },
    },

    button: {
        border: 'none',
        background: 'none',
        outline: 'none',
        padding: 0,
        margin: 0,
        height: 40,
        width: 40,

        '&:disabled': {
            opacity: .3,
        },

        '&:hover': {
            background: theme.colors.icon_background,
            borderRadius: theme.radius / 2,
            cursor: 'pointer',
        },
    },

    tableActionsContainer:{
        minHeight: 72,
        alignContent: 'center',
        marginBottom: theme.spacing(2),
    },
    tableActions: {
        ...flex('row', 'space-between', 'center'),
        padding: `0 ${theme.spacing(4)}`,
        gap: theme.spacing(1.5),
        flexWrap: 'nowrap',
        [theme.down('tablet')]: {
            padding: 0,
            flexWrap: 'wrap',
        },
    },
    tableContainer: {
        height: `90%`,
        [theme.down('mobile')]: {
            height: 'auto',
        },
    },
    filters: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: theme.spacing(1.5),
        flex: '0 1 auto',
        [theme.down('tablet')]: {
            width: '100%',
        },
    },
    summaryPanel: {
        display: 'flex',
        flex: '1 1 32rem',
        maxWidth: '50rem',
        minWidth: 0,
        [theme.down('tablet')]: {
            width: '100%',
            minWidth: 0,
        },
    },
    buttonsWrapper:{
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1.5),
        justifyContent: 'flex-end',
        flexShrink: 0,
        alignItems: 'center',
        [theme.down('tablet')]: {
            width: '100%',
            justifyContent: 'flex-start',
        },
    },
    invite: {
        display: 'flex',
        alignItems: 'center',
    },
    h4: {
        fontFamily: theme.font.secondary,
        fontSize: 20,
        lineHeight: 1.4,
        fontWeight: 'normal',
        color: theme.colors.black,
    },
    addIcon: {
        color: theme.colors.violet_primary,
        cursor: 'pointer',
        marginLeft: theme.spacing(3),
    },

    filterIcon: {
        color: theme.colors.violet_primary,
        cursor: 'pointer',
    },

    searchWrapper: {
        width: 240,
        marginRight: theme.spacing(2),
        [theme.down('tablet')]: {
            width: 'min(100%, 360px)',
            marginRight: 0,
        },
    },

    moreItemsPresent: {
        color: theme.colors.blue_link,
        marginLeft: theme.spacing(2),
        fontFamily: theme.font.secondary,
    },

    noInfo: {
        color: theme.colors.grey_text,
    },

    counter: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 32,
        padding: `${theme.spacing(0.5)} ${theme.spacing(1.25)}`,
        borderRadius: theme.radius,
        color: theme.colors.violet_primary,
        backgroundColor: `${color(theme.colors.violet_primary).fade(0.92)}`,
        whiteSpace: 'nowrap',
    },

    typeBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
        borderRadius: theme.radius,
        color: theme.colors.violet_primary,
        backgroundColor: `${color(theme.colors.violet_primary).fade(0.92)}`,
        fontFamily: theme.font.secondary,
        fontSize: 18,
        lineHeight: 1.2,
        letterSpacing: 2.5,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
    },

    capitalized: {
        textTransform: 'capitalize',
    },

    nameWrapper: {
        ...flex('row', 'flex-start', 'center'),
        minWidth: 0,
    },

    procedureImage: {
        width: 40,
        height: 40,
        objectFit: 'cover',
        marginRight: theme.spacing(3),
        borderRadius: theme.radius,
        backgroundColor: theme.colors.grey_light,
        ...flex('row', 'center', 'center'),
        flexShrink: 0,
    },

    procedureName: {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        [theme.down('mobile')]: {
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
        },
    },

    executionsChip: {
        gap: theme.spacing(1),

        '& svg': {
            fontSize: 16,
        },
    },

    exportButton: {
        marginLeft: 10,
        [theme.down('tablet')]: {
            marginLeft: 0,
        },
    },

    procedureActions: {
        ...flex('row', 'flex-start', 'center'),
        justifyContent: 'flex-start',
        display: 'inline-flex',
        gap: theme.spacing(0.5),
        minWidth: 164,
    },
}));
